import { useEffect, useRef, useState, type MouseEvent, type PointerEvent, type TouchEvent } from "react";
import { useNavigate } from "react-router-dom";
import ImagePreviewModal from "@/components/feature/ImagePreviewModal";
import BottomNav from "@/components/feature/BottomNav";
import { appApi, coachApi } from "@/api";
import type { CoachFilterTagItem } from "@/api/modules/coach";
import { resolveCustomerAssetUrl } from "@/lib/asset";
import { auth } from "@/lib/auth";
import { locationStore, requestCurrentLocation } from "@/lib/location";
import { buildLoginRedirect } from "@/lib/navigation";
import { storage, storageKeys } from "@/lib/storage";
import type { CityItem, CoachListItem } from "@/types/api";

const STATUS_TABS = ["全部技师", "可预约"];

const FILTER_TAGS = [
  { id: "all", label: "全部", icon: "ri-apps-line" },
  { id: "nearby", label: "附近", icon: "ri-map-pin-line" },
  { id: "newbie", label: "新人", icon: "ri-seedling-line" },
  { id: "quality", label: "优质", icon: "ri-shield-star-line" },
  { id: "hot", label: "热销", icon: "ri-fire-line" },
  { id: "highrate", label: "好评", icon: "ri-star-smile-line" },
  { id: "fast", label: "快上门", icon: "ri-flashlight-line" },
  { id: "online", label: "在线", icon: "ri-wireless-charging-line" },
  { id: "female", label: "女技师", icon: "ri-user-heart-line" },
  { id: "senior", label: "资深", icon: "ri-medal-line" },
  { id: "safe", label: "已认证", icon: "ri-verified-badge-line" },
];

const FALLBACK_CITIES = ["重庆", "深圳", "上海", "北京", "广州", "成都"];
const CITY_LOCATING_LABEL = "定位中";
const CITY_SELECT_LABEL = "选择城市";
const HOME_THERAPIST_SCROLL_KEY = "athome:customer:home:therapist-scroll";

const normalizeCityName = (value: string) => value.replace(/市$/, "").trim();

interface CityOption {
  id?: number;
  name: string;
  source?: "manual" | "located";
}

function getRememberedCity(): CityOption | null {
  const stored = storage.get<CityOption>(storageKeys.city);
  const name = String(stored?.name || "").trim();
  const id = Number(stored?.id || 0);
  if (!name) {
    return null;
  }
  return {
    name,
    id: Number.isFinite(id) && id > 0 ? id : undefined,
    source: stored?.source === "located" ? "located" : "manual",
  };
}

function isManualCity(cityOption: CityOption | null) {
  return Boolean(cityOption?.name && cityOption.source !== "located");
}

function getLocationErrorDetail(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  if (error && typeof error === "object") {
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }
  return "";
}

function rememberSelectedCity(cityOption: CityOption, source: "manual" | "located" = "manual") {
  if (cityOption.id) {
    storage.set(storageKeys.city, { id: cityOption.id, name: cityOption.name, source });
    return;
  }
  if (source === "manual" && cityOption.name) {
    storage.set(storageKeys.city, { name: cityOption.name, source });
    return;
  }
  if (!isManualCity(getRememberedCity())) {
    storage.remove(storageKeys.city);
  }
}

interface FilterTagOption {
  key: string;
  label: string;
  icon: string;
  mode: "default" | "backend";
  sort: number;
  tagId?: number;
}

interface TherapistCardItem {
  id: number;
  name: string;
  level: string;
  levelStyle: { color: string; bg: string; border: string };
  status: string;
  statusStyle: { color: string; bg: string };
  rating: number;
  orders: number;
  earliestTime: string;
  distance: string;
  favorited: boolean;
  favorites: number;
  specialties: string[];
  intro: string;
  avatar: string;
  video: string | null;
  photos: string[];
  operationTags: string[];
  operationTagIds: number[];
  freeTravelFee: boolean;
}

interface FavoriteOverride {
  favorited: boolean;
  favorites: number;
}

function getDefaultFilterTags(): FilterTagOption[] {
  const defaults = FILTER_TAGS.filter((item) => item.id !== "all" && item.id !== "nearby");
  return defaults.map((item, index) => ({
    key: item.id,
    label: item.label,
    icon: item.icon,
    mode: "default",
    sort: defaults.length - index,
  }));
}

function pickFilterIcon(name: string) {
  const text = name.toLowerCase();
  if (name.includes("附近") || text.includes("near")) return "ri-map-pin-line";
  if (name.includes("新")) return "ri-seedling-line";
  if (name.includes("优") || name.includes("严选") || name.includes("认证")) return "ri-shield-star-line";
  if (name.includes("热") || name.includes("火")) return "ri-fire-line";
  if (name.includes("评") || name.includes("口碑")) return "ri-star-smile-line";
  if (name.includes("快")) return "ri-flashlight-line";
  if (name.includes("在线")) return "ri-wireless-charging-line";
  if (name.includes("女")) return "ri-user-heart-line";
  if (name.includes("资深") || name.includes("首席") || name.includes("高级")) return "ri-medal-line";
  return "ri-price-tag-3-line";
}

function mapBackendFilterTag(item: CoachFilterTagItem): FilterTagOption | null {
  const id = Number(item.id);
  const name = String(item.name || "").trim();
  const sort = Number(item.sort ?? 0);
  if (!Number.isFinite(id) || !name || name === "附近") {
    return null;
  }
  return {
    key: `tag:${id}`,
    label: name,
    icon: pickFilterIcon(name),
    mode: "backend",
    sort: Number.isFinite(sort) ? sort : 0,
    tagId: id,
  };
}

function sortFilterTags(tags: FilterTagOption[]) {
  return [...tags].sort((left, right) => {
    if (right.sort !== left.sort) {
      return right.sort - left.sort;
    }
    const leftId = left.tagId ?? 0;
    const rightId = right.tagId ?? 0;
    if (rightId !== leftId) {
      return rightId - leftId;
    }
    return left.key.localeCompare(right.key);
  });
}

const AVAILABLE_STATUS_TEXT = "可服务";
const BUSY_STATUS_TEXT = "服务中";
const RESTING_STATUS_TEXT = "休息中";
const PAUSED_STATUS_TEXT = "暂停服务";

const DEFAULT_LEVEL_STYLE = { color: "#8B6FE8", bg: "#F2EEFF", border: "#C9BCFF" };
const NEWBIE_LEVEL_STYLE = { color: "#FF7B22", bg: "#FFF3EC", border: "#FFD4B0" };
const SENIOR_LEVEL_STYLE = { color: "#C8860A", bg: "#FFF8E7", border: "#FFD980" };
const AVAILABLE_STATUS_STYLE = { color: "#17B978", bg: "#E8FBF3" };
const BUSY_STATUS_STYLE = { color: "#FF7B22", bg: "#FFF3EC" };
const PAUSED_STATUS_STYLE = { color: "#909399", bg: "#F4F4F5" };
const NEWBIE_LABEL = "\u65B0\u4EBA";
const QUALITY_TAG_KEYWORD = "\u4F18\u8D28";
const DEFAULT_EARLIEST_TIME = "可尽快安排";

function parseDistance(value: string) {
  const distance = Number.parseFloat(value);
  return Number.isFinite(distance) ? distance : Number.POSITIVE_INFINITY;
}

function normalizeDistance(value?: string) {
  if (!value) {
    return "";
  }
  const distance = Number.parseFloat(value);
  if (!Number.isFinite(distance) || distance <= 0) {
    return "";
  }
  if (distance < 1) {
    return `${Math.max(1, Math.round(distance * 1000))}m`;
  }
  return `${distance.toFixed(distance >= 100 ? 0 : 1)}km`;
}

function getDisplayBadgeText(status: string, earliestTime: string) {
  if (status !== AVAILABLE_STATUS_TEXT) {
    return status;
  }
  const value = earliestTime.trim();
  if (!value || value === "尽快上门") {
    return DEFAULT_EARLIEST_TIME;
  }
  return `最早 ${value}`;
}

function getDistanceText(distance: string) {
  return distance.trim() ? distance : "--";
}

function hasDistanceText(distance: string) {
  return Boolean(distance.trim());
}

function getLevelStyle(level?: string) {
  if (level === NEWBIE_LABEL) return NEWBIE_LEVEL_STYLE;
  if (level === "\u9AD8\u7EA7") return SENIOR_LEVEL_STYLE;
  return DEFAULT_LEVEL_STYLE;
}

function isQualityTherapist(item: TherapistCardItem) {
  const operationTags = Array.isArray(item.operationTags) ? item.operationTags : [];
  return operationTags.some((tag) => String(tag || "").includes(QUALITY_TAG_KEYWORD));
}

function hasQualityOperationTag(item: TherapistCardItem) {
  const operationTags = Array.isArray(item.operationTags) ? item.operationTags : [];
  return operationTags.some((tag) => String(tag || "").includes(QUALITY_TAG_KEYWORD));
}

function isNewbieTherapist(item: TherapistCardItem) {
  const normalizedLevel = String(item.level || "").trim();
  const operationTags = Array.isArray(item.operationTags) ? item.operationTags : [];
  return normalizedLevel === NEWBIE_LABEL || operationTags.some((tag) => String(tag || "").includes(NEWBIE_LABEL));
}

function compareHomeDefault(left: TherapistCardItem, right: TherapistCardItem) {
  const leftPriority = isQualityTherapist(left) ? 0 : isNewbieTherapist(left) ? 1 : 2;
  const rightPriority = isQualityTherapist(right) ? 0 : isNewbieTherapist(right) ? 1 : 2;

  if (leftPriority !== rightPriority) {
    return leftPriority - rightPriority;
  }

  const distanceGap = parseDistance(left.distance) - parseDistance(right.distance);
  if (distanceGap !== 0) {
    return distanceGap;
  }

  if (right.rating !== left.rating) {
    return right.rating - left.rating;
  }

  if (right.orders !== left.orders) {
    return right.orders - left.orders;
  }

  return right.id - left.id;
}

function getDisplayStatusInfo(item: CoachListItem) {
  if (Number(item.serverStatus) !== 1) {
    return { text: PAUSED_STATUS_TEXT, style: PAUSED_STATUS_STYLE };
  }
  if (Number(item.workStatus) === 1) {
    return { text: AVAILABLE_STATUS_TEXT, style: AVAILABLE_STATUS_STYLE };
  }
  return { text: RESTING_STATUS_TEXT, style: BUSY_STATUS_STYLE };
}

function getEarliestBadgeText(status: string, earliestTime: string) {
  if (status !== AVAILABLE_STATUS_TEXT) {
    return status;
  }
  const value = earliestTime.trim();
  if (!value || value === "尽快上门") {
    return DEFAULT_EARLIEST_TIME;
  }
  return `最早 ${value}`;
}

function getStatusInfo(item: CoachListItem) {
  if (Number(item.serverStatus) !== 1) {
    return { text: PAUSED_STATUS_TEXT, style: PAUSED_STATUS_STYLE };
  }
  if (Number(item.workStatus) === 1) {
    return { text: AVAILABLE_STATUS_TEXT, style: AVAILABLE_STATUS_STYLE };
  }
  return { text: RESTING_STATUS_TEXT, style: BUSY_STATUS_STYLE };
}

function mapCoachToCard(item: CoachListItem): TherapistCardItem {
  const level = String(item.level || "中级");
  const status = getDisplayStatusInfo(item);
  const photos = (Array.isArray(item.photos) && item.photos.length ? item.photos : [item.workPhoto || ""].filter(Boolean))
    .map((photo) => resolveCustomerAssetUrl(photo))
    .filter(Boolean);
  const rating = Number(item.rating || 0);
  const avatar = resolveCustomerAssetUrl(String(item.workPhoto || photos[0] || ""));
  return {
    id: item.id,
    name: String(item.name || "技师"),
    level,
    levelStyle: getLevelStyle(level),
    status: status.text,
    statusStyle: status.style,
    rating: Number.isFinite(rating) ? rating : 0,
    orders: Number(item.orderNum || 0),
    earliestTime: String(item.earliestTime || item.first_appoint || DEFAULT_EARLIEST_TIME),
    distance: normalizeDistance(String(item.distanceDesc || "")),
    favorited: Number(item.favorited || 0) === 1,
    favorites: Number(item.favorites || 0),
    specialties: Array.isArray(item.specialties) ? item.specialties : [],
    intro: String(item.serviceSummary || ""),
    avatar,
    video: item.introVideo || null,
    photos,
    operationTags: Array.isArray(item.operationTags) ? item.operationTags.map((tag) => String(tag || "").trim()).filter(Boolean) : [],
    operationTagIds: Array.isArray(item.operationTagIds) ? item.operationTagIds.map((tagId) => Number(tagId)).filter(Number.isFinite) : [],
    freeTravelFee: Number(item.freeTravelFee ?? item.free_travel_fee ?? 0) === 1
      || (Array.isArray(item.operationTags) && item.operationTags.some((tag) => String(tag || "").trim() === "免出行费")),
  };
}

function PhotoGalleryModal({ name, photos, onClose }: { name: string; photos: string[]; onClose: () => void }) {
  return <ImagePreviewModal title={name} images={photos} onClose={onClose} />;
}

function StarRating({ rating }: { rating: number }) {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((index) => (
        <div key={index} className="flex h-3 w-3 items-center justify-center">
          <i
            className={index <= full ? "ri-star-fill" : half && index === full + 1 ? "ri-star-half-fill" : "ri-star-line"}
            style={{ color: "#FF7B22", fontSize: 11 }}
          />
        </div>
      ))}
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const rememberedCityRef = useRef<CityOption | null>(getRememberedCity());
  const [statusTab, setStatusTab] = useState("全部技师");
  const [activeTag, setActiveTag] = useState("all");
  const [filterTags, setFilterTags] = useState<FilterTagOption[]>(() => getDefaultFilterTags());
  const [, setUsesDefaultFilterTags] = useState(true);
  const [city, setCity] = useState(() => rememberedCityRef.current?.name || CITY_LOCATING_LABEL);
  const [showCity, setShowCity] = useState(false);
  const [cities, setCities] = useState<CityOption[]>(FALLBACK_CITIES.map((name) => ({ name })));
  const [selectedCityId, setSelectedCityId] = useState<number | undefined>(undefined);
  const [cityReady, setCityReady] = useState(false);
  const [cityResolved, setCityResolved] = useState(false);
  const [currentCityOpened, setCurrentCityOpened] = useState(true);
  const [locationFailed, setLocationFailed] = useState(false);
  const [locatedCityName, setLocatedCityName] = useState("");
  const [locationGuideVisible, setLocationGuideVisible] = useState(false);
  const [locationGuideMode, setLocationGuideMode] = useState<"prompt" | "help">("prompt");
  const [locationGuideErrorDetail, setLocationGuideErrorDetail] = useState("");
  const [search, setSearch] = useState("");
  const [currentLocation, setCurrentLocation] = useState<ReturnType<typeof locationStore.getFresh>>(() => (
    isManualCity(rememberedCityRef.current) ? locationStore.getFresh() : null
  ));
  const [coachList, setCoachList] = useState<TherapistCardItem[]>([]);
  const [favorites, setFavorites] = useState<Record<number, boolean>>({});
  const [favoriteOverrides, setFavoriteOverrides] = useState<Record<number, FavoriteOverride>>({});
  const [favoritePending, setFavoritePending] = useState<Record<number, boolean>>({});
  const [favoriteFx, setFavoriteFx] = useState<Record<number, "add" | "remove">>({});
  const [loading, setLoading] = useState(false);
  const [gallery, setGallery] = useState<{ name: string; photos: string[] } | null>(null);
  const favoriteOverridesRef = useRef<Record<number, FavoriteOverride>>({});
  const nearbyCityRequestKeyRef = useRef("");
  const autoLocationAttemptedRef = useRef(false);
  const autoLocationPendingRef = useRef(false);
  const listScrollRef = useRef<HTMLDivElement | null>(null);
  const restoredListScrollRef = useRef(false);
  const [autoLocationPending, setAutoLocationPending] = useState(false);
  const onlineCount = coachList.filter((item) => item.status === "可服务").length;

  const openLocationGuide = (mode: "prompt" | "help" = "prompt") => {
    setLocationGuideMode(mode);
    if (mode === "prompt") {
      setLocationGuideErrorDetail("");
    }
    setLocationGuideVisible(true);
  };

  useEffect(() => {
    favoriteOverridesRef.current = favoriteOverrides;
  }, [favoriteOverrides]);

  useEffect(() => {
    setCurrentLocation(locationStore.getFresh());
    return locationStore.subscribe(() => setCurrentLocation(locationStore.getFresh()));
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadFilterTags = async () => {
      try {
        const result = await coachApi.getFilterTags();
        if (cancelled) return;
        const tags = Array.isArray(result) ? sortFilterTags(result.map(mapBackendFilterTag).filter(Boolean) as FilterTagOption[]) : [];
        if (tags.length > 0) {
          setFilterTags(tags);
          setUsesDefaultFilterTags(false);
          return;
        }
      } catch {
        if (cancelled) return;
      }

      if (!cancelled) {
        setFilterTags(sortFilterTags(getDefaultFilterTags()));
        setUsesDefaultFilterTags(true);
      }
    };

    void loadFilterTags();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (activeTag === "all") {
      return;
    }
    if (activeTag === "nearby") {
      return;
    }
    if (!filterTags.some((item) => item.key === activeTag)) {
      setActiveTag("all");
    }
  }, [activeTag, filterTags]);

  useEffect(() => {
    let cancelled = false;

    const loadCities = async () => {
      try {
        const result = await appApi.getCityList();
        if (cancelled) return;

        const options = result
          .map((item: CityItem) => ({
            id: item.id ?? item.city_id,
            name: normalizeCityName(String(item.name || "")),
          }))
          .filter((item) => item.name);

        if (options.length) {
          setCities(options);
          const rememberedCity = rememberedCityRef.current;
          const rememberedMatched = rememberedCity
            ? options.find((item) => {
              if (rememberedCity.id && item.id === rememberedCity.id) {
                return true;
              }
              return item.name === rememberedCity.name;
            })
            : undefined;
          const currentCityName = city === CITY_LOCATING_LABEL || city === CITY_SELECT_LABEL ? "" : city;
          const matched = rememberedMatched || (currentCityName ? options.find((item) => item.name === currentCityName) : undefined);
          if (rememberedCity && !rememberedMatched) {
            rememberedCityRef.current = null;
            storage.remove(storageKeys.city);
          }
          setSelectedCityId(matched?.id);
          if (rememberedMatched) {
            setCity(matched.name);
            setCityResolved(true);
            setCurrentCityOpened(true);
            setLocationFailed(false);
          } else if (matched) {
            setCity(matched.name);
          }
        }
      } catch {
        if (!cancelled) {
          setCities(FALLBACK_CITIES.map((name) => ({ name })));
        }
      } finally {
        if (!cancelled) {
          setCityReady(true);
        }
      }
    };

    void loadCities();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!cityReady) {
      return;
    }
    if (currentLocation?.latitude && currentLocation?.longitude) {
      return;
    }
    if (autoLocationAttemptedRef.current) {
      return;
    }

    autoLocationAttemptedRef.current = true;
    if (!isManualCity(rememberedCityRef.current)) {
      setCity(CITY_SELECT_LABEL);
      setSelectedCityId(undefined);
      setCurrentCityOpened(false);
      setLocationFailed(true);
      setCoachList([]);
      setFavorites({});
      setCityResolved(true);
    }
    openLocationGuide();
  }, [cityReady, currentLocation?.latitude, currentLocation?.longitude]);

  useEffect(() => {
    if (!cityReady) {
      return;
    }
    if (isManualCity(rememberedCityRef.current)) {
      setCityResolved(true);
      setCurrentCityOpened(true);
      return;
    }
    if (!currentLocation?.latitude || !currentLocation?.longitude) {
      if (autoLocationPendingRef.current || autoLocationPending || !autoLocationAttemptedRef.current) {
        setCityResolved(false);
        return;
      }
      if (!isManualCity(rememberedCityRef.current)) {
        setCity(CITY_SELECT_LABEL);
        setSelectedCityId(undefined);
        setCurrentCityOpened(false);
        setLocationFailed(true);
        setCoachList([]);
        setFavorites({});
        setCityResolved(true);
        return;
      }
      setCityResolved(true);
      setCurrentCityOpened(true);
      return;
    }

    const requestKey = `${currentLocation.latitude.toFixed(6)},${currentLocation.longitude.toFixed(6)}`;
    if (nearbyCityRequestKeyRef.current === requestKey) {
      return;
    }
    let cancelled = false;

    const resolveNearbyCity = async () => {
      setCityResolved(false);
      try {
        const nearby = await appApi.getNearbyCity({
          latitude: currentLocation.latitude,
          longitude: currentLocation.longitude,
          accuracy: currentLocation.accuracy,
        });
        if (cancelled) return;
        nearbyCityRequestKeyRef.current = requestKey;
        const currentName = normalizeCityName(String(nearby.current_city_name ?? nearby.currentCityName ?? nearby.name ?? ""));
        const opened = nearby.opened === true;
        const cityId = Number(nearby.city_id ?? nearby.cityId ?? nearby.id ?? 0);
        const matched = cities.find((item) => {
          if (Number.isFinite(cityId) && cityId > 0 && item.id === cityId) {
            return true;
          }
          return currentName && item.name === currentName;
        });

        setLocatedCityName(currentName);
        setLocationFailed(false);
        setCurrentCityOpened(opened && Boolean(matched));

        if (opened && matched) {
          setCity(matched.name);
          setSelectedCityId(matched.id);
          rememberedCityRef.current = { ...matched, source: "located" };
          rememberSelectedCity(matched, "located");
          setActiveTag("all");
          return;
        }

        if (currentName) {
          setCity(currentName);
          setLocationFailed(false);
        } else {
          setCity(CITY_SELECT_LABEL);
          setLocationFailed(true);
        }
        setSelectedCityId(undefined);
        setCoachList([]);
        setFavorites({});
        setLoading(false);
        if (!isManualCity(rememberedCityRef.current)) {
          rememberedCityRef.current = null;
          storage.remove(storageKeys.city);
        }
      } catch {
        if (!cancelled) {
          nearbyCityRequestKeyRef.current = "";
          setCurrentCityOpened(true);
        }
      } finally {
        if (!cancelled) {
          setCityResolved(true);
        }
      }
    };

    void resolveNearbyCity();

    return () => {
      cancelled = true;
    };
  }, [cityReady, cities, currentLocation?.latitude, currentLocation?.longitude]);

  useEffect(() => {
    if (!cityReady || !cityResolved) {
      return;
    }
    if (!currentCityOpened) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadCoaches = async () => {
      setLoading(true);
      try {
        const result = await coachApi.getCoachList({
          pageNum: 1,
          pageSize: 50,
          cityId: selectedCityId,
          longitude: currentLocation?.longitude,
          latitude: currentLocation?.latitude,
          sortBy: activeTag === "nearby" ? "distance_asc" : "home_default",
        });
        if (cancelled) return;
        const cards = Array.isArray(result.records) ? result.records.map(mapCoachToCard) : [];
        const mergedCards = cards.map((item) => {
          const override = favoriteOverridesRef.current[item.id];
          return override ? { ...item, favorited: override.favorited, favorites: override.favorites } : item;
        });
        setCoachList(mergedCards);
        setFavorites((prev) => ({
          ...prev,
          ...Object.fromEntries(mergedCards.map((item) => [item.id, item.favorited])),
        }));
      } catch {
        if (!cancelled) {
          setCoachList([]);
          setFavorites({});
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadCoaches();

    return () => {
      cancelled = true;
    };
  }, [activeTag === "nearby", cityReady, cityResolved, currentCityOpened, selectedCityId, currentLocation?.longitude, currentLocation?.latitude]);

  const selectedFilterTag =
    activeTag === "all"
      ? null
      : activeTag === "nearby"
        ? { key: "nearby", label: "附近", icon: "ri-map-pin-line", mode: "default" as const }
        : filterTags.find((item) => item.key === activeTag) ?? null;

  const filtered = coachList
    .filter((therapist) => (statusTab === "可预约" ? therapist.status === "可服务" : true))
    .filter((therapist) => search.trim() === "" || therapist.name.includes(search.trim()))
    .filter((therapist) => {
      if (!selectedFilterTag) return true;
      if (selectedFilterTag?.mode === "backend") {
        return therapist.operationTagIds.includes(Number(selectedFilterTag.tagId));
      }
      if (selectedFilterTag?.key === "nearby") return true;
      if (activeTag === "newbie") return isNewbieTherapist(therapist);
      if (selectedFilterTag?.key === "quality") return therapist.rating >= 4.6;
      if (selectedFilterTag?.key === "hot") return therapist.orders >= 200;
      if (selectedFilterTag?.key === "highrate") return therapist.rating >= 4.7;
      if (activeTag === "fast") return parseDistance(therapist.distance) < 1.5 && therapist.status === "可服务";
      if (activeTag === "online") return therapist.status === "可服务";
      if (selectedFilterTag?.key === "female") return true;
      if (activeTag === "senior") return therapist.level === "高级";
      if (selectedFilterTag?.key === "safe") return therapist.rating >= 4.5 && therapist.orders >= 100;
      return true;
    })
    .sort((left, right) => {
      if (!selectedFilterTag) return compareHomeDefault(left, right);
      if (selectedFilterTag.mode === "backend") return 0;
      if (selectedFilterTag.key === "nearby") return parseDistance(left.distance) - parseDistance(right.distance);
      if (selectedFilterTag.key === "hot") return right.orders - left.orders;
      if (selectedFilterTag.key === "highrate" || selectedFilterTag.key === "quality") return right.rating - left.rating;
      return 0;
    });

  useEffect(() => {
    if (restoredListScrollRef.current || loading || filtered.length === 0) {
      return;
    }
    restoredListScrollRef.current = true;
    const savedTop = Number(window.sessionStorage.getItem(HOME_THERAPIST_SCROLL_KEY) || 0);
    if (!Number.isFinite(savedTop) || savedTop <= 0) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (listScrollRef.current) {
          listScrollRef.current.scrollTop = savedTop;
        }
        const root = document.getElementById("root");
        if (root) {
          root.scrollTop = savedTop;
        }
        window.scrollTo({ top: savedTop });
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [filtered.length, loading]);

  const rememberTherapistListScroll = () => {
    const containerTop = listScrollRef.current?.scrollTop ?? 0;
    const rootTop = document.getElementById("root")?.scrollTop ?? 0;
    const pageTop = window.scrollY || document.documentElement.scrollTop || 0;
    window.sessionStorage.setItem(HOME_THERAPIST_SCROLL_KEY, String(Math.max(containerTop, rootTop, pageTop)));
  };

  const stopFavoritePress = (event: PointerEvent<HTMLButtonElement> | TouchEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };

  const isInteractiveTarget = (target: EventTarget | null) =>
    target instanceof Element && Boolean(target.closest("button, a, input, textarea, select, [role='button']"));

  const toggleFav = async (id: number, event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!auth.getToken()) {
      navigate(buildLoginRedirect("/"));
      return;
    }
    if (favoritePending[id]) {
      return;
    }

    const target = coachList.find((item) => item.id === id);
    if (!target) return;

    const previousFavorited = Boolean(favorites[id]);
    const nextFavorited = !previousFavorited;
    const delta = nextFavorited ? 1 : -1;
    const optimisticFavorites = Math.max(0, target.favorites + delta);
    const fxType = nextFavorited ? "add" : "remove";

    setFavoriteFx((prev) => ({ ...prev, [id]: fxType }));
    window.setTimeout(() => {
      setFavoriteFx((prev) => {
        if (prev[id] !== fxType) {
          return prev;
        }
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }, 360);

    setFavorites((prev) => ({ ...prev, [id]: nextFavorited }));
    setFavoriteOverrides((prev) => ({
      ...prev,
      [id]: { favorited: nextFavorited, favorites: optimisticFavorites },
    }));
    setFavoritePending((prev) => ({ ...prev, [id]: true }));
    setCoachList((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              favorited: nextFavorited,
              favorites: optimisticFavorites,
            }
          : item,
      ),
    );

    try {
      const result = await appApi.toggleCollect({ id, type: 1 });
      const actualFavorited = Number(result.is_collect || 0) === 1;
      const actualFavorites = Math.max(0, target.favorites + (actualFavorited === previousFavorited ? 0 : actualFavorited ? 1 : -1));
      setFavorites((prev) => ({ ...prev, [id]: actualFavorited }));
      setFavoriteOverrides((prev) => ({
        ...prev,
        [id]: { favorited: actualFavorited, favorites: actualFavorites },
      }));
      setCoachList((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                favorited: actualFavorited,
                favorites: actualFavorites,
              }
          : item,
        ),
      );
    } catch {
      setFavorites((prev) => ({ ...prev, [id]: previousFavorited }));
      setFavoriteOverrides((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setCoachList((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                favorited: previousFavorited,
                favorites: target.favorites,
              }
            : item,
        ),
      );
    } finally {
      setFavoritePending((prev) => ({ ...prev, [id]: false }));
    }
  };

  const goToDetail = (therapist: TherapistCardItem) => {
    rememberTherapistListScroll();
    navigate(`/therapist-detail?id=${therapist.id}`, {
      state: {
        previewImage: therapist.avatar,
        previewName: therapist.name,
      },
    });
  };

  const requestHomeLocation = async () => {
    setAutoLocationPending(true);
    autoLocationPendingRef.current = true;
    setLocationGuideErrorDetail("");
    setCityResolved(false);
    try {
      const location = await requestCurrentLocation();
      setCurrentLocation(location);
      setLocationFailed(false);
      setLocationGuideVisible(false);
      setLocationGuideMode("prompt");
      rememberedCityRef.current = null;
      storage.remove(storageKeys.city);
      return true;
    } catch (error) {
      setCity(CITY_SELECT_LABEL);
      setSelectedCityId(undefined);
      setCurrentCityOpened(false);
      setLocationFailed(true);
      setCityResolved(true);
      setLocationGuideErrorDetail(getLocationErrorDetail(error));
      openLocationGuide("help");
      return false;
    } finally {
      autoLocationPendingRef.current = false;
      setAutoLocationPending(false);
    }
  };

  const handleNearbyFilterClick = async () => {
    if (!currentLocation?.latitude || !currentLocation?.longitude) {
      openLocationGuide();
      return;
    }
    setActiveTag("nearby");
  };

  return (
    <div className="customer-page-shell flex min-h-screen flex-col" style={{ background: "#F4F4F6", overflowX: "clip" }}>
      <div className="sticky top-0 z-40 flex-shrink-0" style={{ background: "#F4F4F6" }}>
      <div style={{ background: "linear-gradient(135deg, #FF8C3A 0%, #FF6010 100%)", paddingTop: 14 }}>
        <div className="flex min-w-0 items-center gap-2.5 px-4 pb-3">
          <div className="relative flex-shrink-0">
            <button onClick={() => setShowCity(!showCity)} className="flex max-w-[30vw] min-w-0 items-center gap-1 whitespace-nowrap">
              <div className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-map-pin-fill" style={{ color: "rgba(255,255,255,0.9)", fontSize: 13 }} />
              </div>
              <span className="truncate text-sm font-bold text-white">{city}</span>
              <div className="flex h-4 w-4 items-center justify-center">
                <i
                  className={showCity ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}
                  style={{ color: "rgba(255,255,255,0.8)", fontSize: 14 }}
                />
              </div>
            </button>
            {showCity ? (
              <div
                className="absolute z-50 overflow-hidden rounded-2xl bg-white"
                style={{ top: 34, left: -8, minWidth: 108, boxShadow: "0 8px 32px rgba(255,96,16,0.18)" }}
              >
                <div className="px-3 py-2 text-xs font-semibold" style={{ color: "#999", borderBottom: "1px solid #F5F5F5" }}>
                  选择城市
                </div>
                {cities.map((item) => (
                  <button
                    key={`${item.id || item.name}`}
                    onClick={() => {
                      rememberedCityRef.current = { ...item, source: "manual" };
                      setCity(item.name);
                      setSelectedCityId(item.id);
                      setCurrentCityOpened(true);
                      setLocationFailed(false);
                      setCityResolved(true);
                      setLocatedCityName("");
                      setLoading(false);
                      rememberSelectedCity(item, "manual");
                      setShowCity(false);
                    }}
                    className="flex w-full min-w-0 items-center justify-between gap-3 px-3 py-2.5"
                    style={{
                      background: city === item.name ? "#FFF3EC" : "transparent",
                      borderBottom: "1px solid #FAF9F9",
                    }}
                  >
                      <span className="truncate text-sm font-medium" style={{ color: city === item.name ? "#FF7B22" : "#333" }}>
                        {item.name}
                      </span>
                    {city === item.name ? <i className="ri-check-line text-xs" style={{ color: "#FF7B22" }} /> : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div
            className="flex min-w-0 flex-1 items-center gap-2 px-3.5 py-2.5"
            style={{ background: "rgba(255,255,255,0.18)", borderRadius: 24, border: "1px solid rgba(255,255,255,0.3)" }}
          >
            <i className="ri-search-line" style={{ color: "rgba(255,255,255,0.8)", fontSize: 14 }} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="搜索技师姓名"
              className="min-w-0 flex-1 bg-transparent text-white outline-none placeholder-white/60"
              style={{ fontSize: 13 }}
            />
            {search ? (
              <button onClick={() => setSearch("")}>
                <i className="ri-close-circle-fill" style={{ color: "rgba(255,255,255,0.6)", fontSize: 14 }} />
              </button>
            ) : null}
          </div>
          <button
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl"
            style={{ background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.3)" }}
          >
            <i className="ri-equalizer-3-line text-base text-white" />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 px-4 pb-3.5">
          <div className="min-w-0 flex-1 overflow-x-auto" style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
            <div className="flex w-max items-center p-1" style={{ background: "rgba(0,0,0,0.18)", borderRadius: 24 }}>
            {STATUS_TABS.map((tab) => {
              const active = statusTab === tab;
              return (
                <button
                  key={tab}
                  onClick={() => setStatusTab(tab)}
                  className="whitespace-nowrap transition-all duration-200"
                  style={{ padding: "5px 18px", borderRadius: 20, background: active ? "#fff" : "transparent" }}
                >
                  <span className="text-sm font-semibold" style={{ color: active ? "#FF6010" : "rgba(255,255,255,0.75)" }}>
                    {tab}
                  </span>
                </button>
              );
            })}
            </div>
          </div>
          <div className="ml-auto flex flex-shrink-0 items-center gap-1.5">
            <span
              className="inline-block rounded-full"
              style={{ width: 6, height: 6, background: "#4ADE80", boxShadow: "0 0 0 2px rgba(74,222,128,0.3)" }}
            />
            <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 12, fontWeight: 500 }}>{onlineCount}位技师在线</span>
          </div>
        </div>
      </div>

      <div className="bg-white" style={{ borderBottom: "1px solid #F0F0F0" }}>
        <div className="flex items-center gap-2 px-3 py-2.5">
          <button
            onClick={() => setActiveTag("all")}
            className="flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap transition-all duration-200"
            style={{
              padding: "5px 12px",
              borderRadius: 20,
              background: activeTag === "all" ? "linear-gradient(135deg, #FF8C3A 0%, #FF5C00 100%)" : "#FFF3EC",
              border: activeTag === "all" ? "none" : "1px solid #FFD4B0",
              transform: activeTag === "all" ? "scale(1.05)" : "scale(1)",
            }}
          >
            <i className={FILTER_TAGS[0].icon} style={{ color: activeTag === "all" ? "#fff" : "#FF7B22", fontSize: 12 }} />
            <span className="text-xs font-semibold" style={{ color: activeTag === "all" ? "#fff" : "#FF7B22" }}>
              {FILTER_TAGS[0].label}
            </span>
          </button>

          <div className="min-w-0 flex-1 overflow-x-auto" style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
            <div className="flex min-w-max items-center gap-2 pr-3">
              <button
                key="nearby"
                onClick={() => void handleNearbyFilterClick()}
                className="flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap transition-all duration-200"
                style={{
                  padding: "5px 12px",
                  borderRadius: 20,
                  background: activeTag === "nearby" ? "linear-gradient(135deg, #FF8C3A 0%, #FF5C00 100%)" : "#FFF3EC",
                  border: activeTag === "nearby" ? "none" : "1px solid #FFD4B0",
                  transform: activeTag === "nearby" ? "scale(1.05)" : "scale(1)",
                }}
              >
                <i className="ri-map-pin-line" style={{ color: activeTag === "nearby" ? "#fff" : "#FF7B22", fontSize: 12 }} />
                <span className="text-xs font-semibold" style={{ color: activeTag === "nearby" ? "#fff" : "#FF7B22" }}>
                  附近
                </span>
              </button>

              {filterTags.map((tag) => {
                const active = activeTag === tag.key;
                return (
                  <button
                    key={tag.key}
                    onClick={() => setActiveTag(tag.key)}
                    className="flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap transition-all duration-200"
                    style={{
                      padding: "5px 12px",
                      borderRadius: 20,
                      background: active ? "linear-gradient(135deg, #FF8C3A 0%, #FF5C00 100%)" : "#FFF3EC",
                      border: active ? "none" : "1px solid #FFD4B0",
                      transform: active ? "scale(1.05)" : "scale(1)",
                    }}
                  >
                    <i className={tag.icon} style={{ color: active ? "#fff" : "#FF7B22", fontSize: 12 }} />
                    <span className="text-xs font-semibold" style={{ color: active ? "#fff" : "#FF7B22" }}>
                      {tag.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center px-4 py-2.5" style={{ background: "#F4F4F6" }}>
        <p className="text-xs font-medium" style={{ color: "#AAAAAA" }}>
          {currentCityOpened ? (
            <>
              共找到<span style={{ color: "#FF7B22", fontWeight: 700 }}>{filtered.length}</span>位技师
            </>
          ) : locationFailed ? (
            <span>定位失败，请选择城市</span>
          ) : (
            <span>当前定位城市暂未开通</span>
          )}
        </p>
        {!currentLocation?.latitude || !currentLocation?.longitude ? (
          <button
            type="button"
            onClick={() => openLocationGuide()}
            className="ml-auto flex flex-shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
            style={{ background: "#FFF3EC", color: "#FF7B22" }}
          >
            <i className="ri-map-pin-user-line" />
            开启定位
          </button>
        ) : null}
      </div>
      </div>

      <div
        ref={listScrollRef}
        className="flex flex-1 flex-col gap-2.5 overflow-y-auto px-3"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 88px)" }}
      >
        {!cityResolved || loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full" style={{ background: "#FFF3EC" }}>
              <i className="ri-loader-4-line text-3xl animate-spin" style={{ color: "#FFB380" }} />
            </div>
            <p className="text-sm font-medium" style={{ color: "#BBBBBB" }}>
              正在加载技师列表
            </p>
          </div>
        ) : !currentCityOpened ? (
          <div className="flex flex-1 flex-col items-center justify-center px-8 py-24 text-center">
            <div className="mb-4 flex h-[72px] w-[72px] items-center justify-center rounded-full" style={{ background: "#FFF3EC" }}>
              <i className="ri-map-pin-2-line text-4xl" style={{ color: "#FF8C3A" }} />
            </div>
            <p className="text-base font-bold" style={{ color: "#333" }}>
              {locationFailed ? "无法获取当前位置" : "当前城市未开通"}
            </p>
            <p className="mt-2 text-sm leading-6" style={{ color: "#999" }}>
              {locationFailed ? "请开启微信定位权限，或手动选择已开通城市" : `${locatedCityName || city} 暂未开通服务，请选择其他城市`}
            </p>
            <button
              type="button"
              onClick={() => setShowCity(true)}
              className="mt-5 rounded-full px-5 py-2.5 text-sm font-semibold text-white"
              style={{ background: "linear-gradient(135deg, #FF8C3A 0%, #FF5C00 100%)" }}
            >
              选择其他城市
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full" style={{ background: "#FFF3EC" }}>
              <i className="ri-user-search-line text-3xl" style={{ color: "#FFB380" }} />
            </div>
            <p className="text-sm font-medium" style={{ color: "#BBBBBB" }}>
              暂无符合条件的技师
            </p>
          </div>
        ) : (
          filtered.map((therapist) => {
            const isFav = favorites[therapist.id];
            const hasDistance = hasDistanceText(therapist.distance);
            const favoriteFxType = favoriteFx[therapist.id];
            const favoriteLocked = Boolean(favoritePending[therapist.id]);
            const isNew = isNewbieTherapist(therapist);
            const isTop = !isNew && hasQualityOperationTag(therapist);
            const isDefaultCard = !isNew && !isTop;

            return (
              <div
                key={therapist.id}
                className="relative flex w-full cursor-pointer flex-col text-left"
                style={{
                  borderRadius: isDefaultCard ? 18 : 16,
                  overflow: "hidden",
                  border: isTop ? "1.5px solid #F0D060" : isNew ? "1.5px solid #FFD4B0" : "1px solid rgba(255, 130, 48, 0.18)",
                  background: isTop ? "linear-gradient(135deg, #FFFDF0 0%, #FFF8E0 100%)" : isDefaultCard ? "linear-gradient(135deg, #FFFFFF 0%, #FFF8F2 100%)" : "#fff",
                  boxShadow: isDefaultCard ? "0 10px 24px rgba(143, 86, 35, 0.10), 0 1px 0 rgba(255,255,255,0.9) inset" : "none",
                }}
                onClick={(event) => {
                  if (isInteractiveTarget(event.target)) {
                    return;
                  }
                  goToDetail(therapist);
                }}
              >
                {isNew ? (
                  <div className="flex min-w-0 w-full items-center gap-1.5 px-3 py-1.5" style={{ background: "linear-gradient(90deg, #FF8C3A 0%, #FF5C00 100%)" }}>
                    <i className="ri-seedling-line text-white" style={{ fontSize: 11 }} />
                    <span className="min-w-0 flex-1 truncate text-[11px] font-bold text-white">新人技师 · 服务用心· 专注细致</span>
                    <i className="ri-arrow-right-s-line ml-auto flex-shrink-0 text-white/70" style={{ fontSize: 12 }} />
                  </div>
                ) : null}

                {isTop ? (
                  <div className="flex min-w-0 w-full items-center gap-1.5 px-3 py-1.5" style={{ background: "linear-gradient(90deg, #F5C518 0%, #E8A800 100%)" }}>
                    <i className="ri-vip-crown-fill" style={{ color: "#fff", fontSize: 11 }} />
                    <span className="min-w-0 flex-1 truncate text-[11px] font-bold text-white">优质技师 · 严选认证 · 服务有保障</span>
                    <i className="ri-shield-check-fill ml-auto flex-shrink-0 text-white/80" style={{ fontSize: 12 }} />
                  </div>
                ) : null}

                {isDefaultCard ? (
                  <div className="h-1 w-full" style={{ background: "linear-gradient(90deg, #FFE6D1 0%, #FF8A3D 42%, #FFE8D7 100%)" }} />
                ) : null}

                <div className="flex items-stretch" style={{ minHeight: isDefaultCard ? 154 : undefined }}>
                  <div
                    className="relative flex-shrink-0 overflow-hidden"
                    style={{
                      width: "clamp(108px, 36vw, 130px)",
                      background: isDefaultCard ? "linear-gradient(180deg, #FFF7F0 0%, #EEE8E2 100%)" : "#F7F7F7",
                      borderTopRightRadius: isDefaultCard ? 14 : 0,
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      setGallery({ name: therapist.name, photos: therapist.photos });
                    }}
                  >
                    <img
                      src={therapist.avatar}
                      alt={therapist.name}
                      className="h-full w-full object-cover object-top"
                      style={{
                        minHeight: isDefaultCard ? 154 : 148,
                        objectPosition: "50% top",
                        display: "block",
                        imageRendering: "auto",
                      }}
                    />
                    <div
                      className="absolute bottom-0 left-0 right-0 flex items-center justify-center pb-1.5"
                      style={{ height: isDefaultCard ? 30 : 32, background: "linear-gradient(to top, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.12) 58%, transparent 100%)" }}
                    >
                      <div
                        className="flex items-center gap-1 rounded-full px-2 py-0.5"
                        style={{ background: isDefaultCard ? "rgba(0,0,0,0.22)" : "transparent" }}
                      >
                        <i className="ri-image-2-line" style={{ color: "rgba(255,255,255,0.85)", fontSize: 11 }} />
                        <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 10, fontWeight: 500 }}>{therapist.photos.length}张</span>
                      </div>
                    </div>
                  </div>

                  <div
                    className="relative flex min-w-0 flex-1 flex-col justify-between px-3 py-3"
                    style={{
                      background: isDefaultCard ? "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(255,248,242,0.86) 100%)" : undefined,
                    }}
                  >
                    {isDefaultCard ? (
                      <div
                        className="pointer-events-none absolute right-0 top-0 h-16 w-20"
                        style={{ background: "radial-gradient(circle at 100% 0%, rgba(255, 126, 36, 0.11) 0%, rgba(255, 126, 36, 0) 70%)" }}
                      />
                    ) : null}
                    <div className="flex min-w-0 items-start justify-between gap-2">
                      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                        <span className="truncate text-[15px] font-bold tracking-wide" style={{ color: "#171717" }}>
                          {therapist.name}
                        </span>
                        <span
                          className="rounded-md px-1.5 py-0.5 font-bold"
                          style={{
                            background: therapist.levelStyle.bg,
                            color: therapist.levelStyle.color,
                            border: `1px solid ${therapist.levelStyle.border}`,
                            fontSize: 10,
                          }}
                        >
                          {therapist.level}
                        </span>
                      </div>
                      <div className="ml-1 flex flex-shrink-0 flex-wrap justify-end gap-1">
                        <span
                          className="flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold"
                          style={{
                            background: therapist.statusStyle.bg,
                            color: therapist.statusStyle.color,
                            boxShadow: isDefaultCard ? "0 4px 10px rgba(23, 185, 120, 0.10)" : undefined,
                          }}
                        >
                          <span
                            className="inline-block flex-shrink-0 rounded-full"
                            style={{
                              width: 5,
                              height: 5,
                              background: therapist.statusStyle.color,
                            }}
                          />
                          {therapist.status}
                        </span>
                        {therapist.freeTravelFee ? (
                          <span
                            className="flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold"
                            style={{ background: "#EAF8F0", color: "#16824B" }}
                          >
                            <i className="ri-coupon-3-line" style={{ fontSize: 11 }} />
                            免出行费
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div className="mt-1.5 flex items-center gap-2">
                      <StarRating rating={therapist.rating} />
                      <span className="text-xs font-bold" style={{ color: "#FF7B22" }}>
                        {therapist.rating.toFixed(1)}
                      </span>
                      <span className="text-xs" style={{ color: "#BBBBBB" }}>
                        · {therapist.orders}单
                      </span>
                    </div>

                    <div className="mt-2 flex flex-wrap gap-1">
                      {therapist.specialties.slice(0, 3).map((item) => (
                        <span
                          key={item}
                          className="whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium"
                          style={{
                            background: isTop ? "#FFF8DC" : isDefaultCard ? "rgba(255, 244, 236, 0.92)" : "#F5F5F7",
                            color: isTop ? "#B07800" : isDefaultCard ? "#9B6A43" : "#999",
                            border: isDefaultCard ? "1px solid rgba(255, 218, 190, 0.55)" : undefined,
                            fontSize: 10,
                          }}
                        >
                          {item}
                        </span>
                      ))}
                    </div>

                    <div className="mt-2 flex min-w-0 items-center justify-between gap-2">
                      <div
                        className="flex min-w-0 items-center gap-1 rounded-lg px-2 py-1"
                        style={{
                          background: isDefaultCard ? "linear-gradient(90deg, #FFF4E9 0%, #FFFFFF 100%)" : "#FFF3EC",
                          border: isDefaultCard ? "1px solid rgba(255, 213, 179, 0.55)" : undefined,
                        }}
                      >
                        <i className="ri-time-line" style={{ color: "#FF7B22", fontSize: 11 }} />
                        <span className="truncate whitespace-nowrap text-xs font-medium" style={{ color: "#FF7B22", fontSize: 11 }}>
                          {getDisplayBadgeText(therapist.status, therapist.earliestTime)}
                        </span>
                      </div>
                      <div
                        className="flex flex-shrink-0 items-center gap-1 rounded-full px-2 py-1"
                        style={{
                          background: hasDistance ? (isDefaultCard ? "#FFFFFF" : "#F6F6F7") : "#F8FAFC",
                          border: hasDistance ? (isDefaultCard ? "1px solid #F0ECE8" : "1px solid transparent") : "1px solid #E8EDF3",
                          boxShadow: isDefaultCard && hasDistance ? "0 4px 10px rgba(80, 66, 52, 0.04)" : undefined,
                        }}
                      >
                        <span
                          className="flex h-4 w-4 items-center justify-center rounded-full"
                          style={{ background: hasDistance ? "#FFFFFF" : "#EEF2F6" }}
                        >
                          <i
                            className={hasDistance ? "ri-map-pin-2-line" : "ri-navigation-line"}
                            style={{
                              color: hasDistance ? "#B9B9B9" : "#AAB4BF",
                              fontSize: hasDistance ? 11 : 10,
                            }}
                          />
                        </span>
                        <span
                          className="whitespace-nowrap text-xs"
                          style={{
                            color: hasDistance ? "#BBBBBB" : "#AAB4BF",
                            letterSpacing: hasDistance ? "0" : "0.08em",
                            minWidth: 16,
                            textAlign: "center",
                          }}
                        >
                          {getDistanceText(therapist.distance)}
                        </span>
                      </div>
                    </div>

                    <div className="mt-2.5 flex min-w-0 items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={(event) => toggleFav(therapist.id, event)}
                        onTouchStart={stopFavoritePress}
                        onPointerDown={stopFavoritePress}
                        aria-disabled={favoriteLocked}
                        className="relative z-10 flex min-h-9 flex-shrink-0 items-center gap-1 rounded-full px-2 py-1 disabled:opacity-60"
                        style={{
                          background: isFav ? "rgba(255, 77, 109, 0.08)" : isDefaultCard ? "rgba(255,255,255,0.68)" : "transparent",
                          transition: "background 0.22s ease, opacity 0.22s ease",
                          minWidth: 52,
                          touchAction: "manipulation",
                          WebkitTouchCallout: "none",
                          userSelect: "none",
                          cursor: favoriteLocked ? "default" : "pointer",
                          opacity: favoriteLocked ? 0.72 : 1,
                          border: isDefaultCard && !isFav ? "1px solid rgba(238, 232, 226, 0.78)" : undefined,
                        }}
                      >
                        <i
                          className={[
                            isFav ? "ri-heart-3-fill" : "ri-heart-3-line",
                            "customer-favorite-icon",
                            favoriteFxType ? `customer-favorite-icon--${favoriteFxType}` : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          style={{ color: isFav ? "#FF4D6D" : "#D0D0D0", fontSize: 15 }}
                        />
                        <span
                          className={[
                            "customer-favorite-count",
                            favoriteFxType ? `customer-favorite-count--${favoriteFxType}` : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          style={{ color: isFav ? "#FF7A93" : "#CCCCCC", fontSize: 11 }}
                        >
                          {therapist.favorites}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          rememberTherapistListScroll();
                          navigate(`/therapist-detail?id=${therapist.id}&showService=true`, {
                            state: {
                              previewImage: therapist.avatar,
                              previewName: therapist.name,
                            },
                          });
                        }}
                        className="flex min-w-0 flex-shrink items-center gap-1.5 rounded-full px-4 py-2 text-white"
                        style={{
                          background: isTop ? "linear-gradient(90deg, #F5C518 0%, #E0A000 100%)" : "linear-gradient(90deg, #FF8C3A 0%, #FF5C00 100%)",
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: 0,
                          boxShadow: isDefaultCard ? "0 8px 16px rgba(255, 108, 20, 0.24)" : undefined,
                        }}
                      >
                        {isTop ? (
                          <>
                            <i className="ri-vip-crown-line" style={{ fontSize: 11 }} />
                            立即预约
                          </>
                        ) : (
                          "立即预约"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}

        {filtered.length > 0 ? (
          <div className="flex items-center justify-center gap-2 py-4">
            <div style={{ flex: 1, height: 1, background: "#EEEEEE" }} />
            <span className="whitespace-nowrap px-2 text-xs" style={{ color: "#CCCCCC" }}>
              已加载全部技师
            </span>
            <div style={{ flex: 1, height: 1, background: "#EEEEEE" }} />
          </div>
        ) : null}
      </div>

      <BottomNav />

      {gallery ? <PhotoGalleryModal name={gallery.name} photos={gallery.photos} onClose={() => setGallery(null)} /> : null}
      {locationGuideVisible ? (
        <div
          className="fixed inset-0 z-[130] flex items-end justify-center"
          style={{ background: "rgba(18, 18, 18, 0.45)" }}
        >
          <div
            className="w-full rounded-t-[28px] bg-white px-5 pt-5"
            style={{
              maxWidth: 480,
              maxHeight: "86vh",
              overflowY: "auto",
              paddingBottom: "calc(24px + env(safe-area-inset-bottom))",
              boxShadow: "0 -12px 40px rgba(0,0,0,0.16)",
            }}
          >
            <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-[#E7E2DA]" />
            <div
              className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl"
              style={{ background: "linear-gradient(135deg, #FFF0E5 0%, #FFE0C2 100%)" }}
            >
              <i className="ri-map-pin-user-fill text-2xl text-[#FF6A00]" />
            </div>
            <h3 className="text-lg font-bold text-[#1A1A1A]">允许获取当前位置</h3>
            <p className="mt-2 text-sm leading-6 text-[#666666]">
              {locationGuideMode === "help"
                ? "当前没有获取到位置权限，暂时无法按真实位置计算技师距离。如果系统没有再次弹出授权框，请在微信网页右上角“...”的设置里确认允许位置信息。"
                : "开启定位后，将为你优先推荐附近可服务技师，并展示距离信息。"}
            </p>
            {locationGuideMode === "help" ? (
              <div className="mt-4 rounded-2xl px-3 py-3 text-xs leading-5" style={{ background: "#F8F5F1", color: "#8B7D6B" }}>
                若手机系统里微信位置权限已经开启，请关闭当前网页，从公众号菜单重新进入后再点“允许定位”。
              </div>
            ) : null}
            {locationGuideMode === "help" && locationGuideErrorDetail ? (
              <div className="mt-3 rounded-2xl px-3 py-3 text-xs leading-5" style={{ background: "#FFF4EC", color: "#C64A12" }}>
                定位错误详情：{locationGuideErrorDetail}
              </div>
            ) : null}
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => setLocationGuideVisible(false)}
                className="flex-1 rounded-full border border-[#E8E1D7] py-3 text-sm font-semibold text-[#666666]"
              >
                稍后再说
              </button>
              <button
                type="button"
                onClick={() => void requestHomeLocation()}
                disabled={autoLocationPending}
                className="flex-1 rounded-full py-3 text-sm font-semibold text-white disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #FF9348 0%, #FF6400 100%)" }}
              >
                {autoLocationPending ? "定位中..." : "允许定位"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
